import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import aviso, { textoDoErro } from '../lib/aviso';
import { LuEye, LuHistory, LuPlus, LuSearch, LuTrash2 } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection, Switch } from '../components/RecordPanel';
import { ListRow, RowAction, RowActions, ListCard, ListCards } from '../components/ListKit';
import { useCelular } from '../hooks/useCelular';
import Dialog from '../components/Dialog';
import Select from '../components/Select';
import '../components/ListKit.css';
import './FuncionalidadesSistemaPage.css';
import {
    FuncionalidadesSistemaService, ROTULO_ESTADO,
    type EstadoFuncionalidade, type Funcionalidade, type FuncionalidadesLista, type MudancaFuncionalidade,
} from '../services/FuncionalidadesSistemaService';
import { EmpresaService, type Empresa } from '../services/EmpresaService';

/*
 * Sistema › Funcionalidades — Direção A de
 * docs/designsystem/propostas/admin/fase-6-tela.html (fechada em 26/09).
 *
 * Lista e painel, no molde da casa, com um desvio de propósito: o painel NÃO
 * tem "Editar". Cada mudança é uma ação inteira de produção, gravada na hora,
 * com a sua confirmação e a sua linha no histórico — juntar várias num Salvar
 * esconderia o efeito de cada uma.
 *
 * Toda mudança do estado GLOBAL pede confirmação com os números (decisão T3);
 * exceção de uma empresa não pede, porque o efeito é uma empresa e está escrito
 * na própria linha.
 */

type Filtro = 'todas' | 'ligada' | 'so_escolhidas' | 'desligada' | 'com_excecao';

const FILTROS: { id: Filtro; rotulo: string }[] = [
    { id: 'todas', rotulo: 'Todas' },
    { id: 'ligada', rotulo: 'Ligadas' },
    { id: 'so_escolhidas', rotulo: 'Só escolhidas' },
    { id: 'desligada', rotulo: 'Desligadas' },
    { id: 'com_excecao', rotulo: 'Com exceção' },
];

const ESTADOS: EstadoFuncionalidade[] = ['ligada', 'so_escolhidas', 'desligada'];

/** O que cada estado quer dizer, em uma frase — é o texto embaixo das pílulas. */
const SIGNIFICADO: Record<EstadoFuncionalidade, string> = {
    ligada: 'Todas as empresas têm, menos as desligadas abaixo.',
    so_escolhidas: 'Ninguém tem, menos as empresas ligadas abaixo. É o estado de quem espera um terceiro: a funcionalidade está pronta, mas só algumas empresas podem usar.',
    desligada: 'Ninguém tem, nem com exceção. É a emergência: as exceções abaixo ficam guardadas e sem efeito.',
};

const COLUNAS = '260px 180px 150px 150px 100px minmax(0, 1fr) 64px';

const dataCurta = (iso: string) => {
    const d = new Date(iso);
    return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

/** Quantas empresas teriam acesso com o estado `novo` — a mesma conta da API. */
const acessoCom = (f: Funcionalidade, novo: EstadoFuncionalidade, total: number) => {
    if (novo === 'desligada') return 0;
    if (novo === 'ligada') return total - f.excecoes.filter(e => !e.ligada).length;
    return f.excecoes.filter(e => e.ligada).length;
};

const frasesDoHistorico = (m: MudancaFuncionalidade) => {
    const quem = m.usuarioNome ?? 'Alguém';
    if (m.empresaId === null) {
        const depois = ROTULO_ESTADO[m.depois as EstadoFuncionalidade] ?? m.depois;
        const antes = ROTULO_ESTADO[m.antes as EstadoFuncionalidade] ?? m.antes;
        return <>{quem} mudou para <b>{depois?.toLowerCase()}</b> <span className="sis-fn-fnt">(era {antes?.toLowerCase()})</span></>;
    }
    const empresa = <b>{m.empresaNome ?? `empresa ${m.empresaId}`}</b>;
    if (m.depois === 'excecao_ligada') return <>{quem} ligou para {empresa}</>;
    if (m.depois === 'excecao_desligada') return <>{quem} desligou para {empresa}</>;
    return <>{quem} tirou a exceção de {empresa}</>;
};

const FuncionalidadesSistemaPage = () => {
    const celular = useCelular();
    const [lista, setLista] = useState<FuncionalidadesLista | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [busca, setBusca] = useState('');
    const [filtro, setFiltro] = useState<Filtro>('todas');
    const [searchParams, setSearchParams] = useSearchParams();
    const chaveAberta = searchParams.get('chave');

    const [historico, setHistorico] = useState<MudancaFuncionalidade[]>([]);
    const [historicoTodo, setHistoricoTodo] = useState(false);
    const [empresas, setEmpresas] = useState<Empresa[]>([]);
    const [adicionando, setAdicionando] = useState(false);
    const [trabalhando, setTrabalhando] = useState(false);
    const [confirmar, setConfirmar] = useState<EstadoFuncionalidade | null>(null);

    const carregar = useCallback(async () => {
        try {
            setLista(await FuncionalidadesSistemaService.listar());
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível carregar as funcionalidades.'));
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { void carregar(); }, [carregar]);

    const aberta = useMemo(
        () => lista?.funcionalidades.find(f => f.chave === chaveAberta) ?? null,
        [lista, chaveAberta],
    );

    const carregarHistorico = useCallback(async (chave: string, todo: boolean) => {
        try {
            setHistorico(await FuncionalidadesSistemaService.historico(chave, todo ? 100 : 5));
        } catch {
            setHistorico([]);
        }
    }, []);

    useEffect(() => {
        setAdicionando(false);
        setHistoricoTodo(false);
        if (chaveAberta) void carregarHistorico(chaveAberta, false);
    }, [chaveAberta, carregarHistorico]);

    // A lista de empresas só serve para "Adicionar empresa": carrega na primeira vez que alguém pede.
    useEffect(() => {
        if (!adicionando || empresas.length > 0) return;
        EmpresaService.buscarTodas()
            .then(r => { if (r.sucesso) setEmpresas(r.dados ?? []); })
            .catch(() => aviso.erro('Não foi possível carregar as empresas.'));
    }, [adicionando, empresas.length]);

    const abrir = (chave: string) => setSearchParams({ chave }, { replace: false });
    const fechar = () => setSearchParams({}, { replace: false });

    const contagens = useMemo(() => {
        const fs = lista?.funcionalidades ?? [];
        return {
            todas: fs.length,
            ligada: fs.filter(f => f.estado === 'ligada').length,
            so_escolhidas: fs.filter(f => f.estado === 'so_escolhidas').length,
            desligada: fs.filter(f => f.estado === 'desligada').length,
            com_excecao: fs.filter(f => f.excecoes.length > 0).length,
        } as Record<Filtro, number>;
    }, [lista]);

    const visiveis = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        return (lista?.funcionalidades ?? [])
            .filter(f => filtro === 'todas'
                || (filtro === 'com_excecao' ? f.excecoes.length > 0 : f.estado === filtro))
            .filter(f => !termo || f.nome.toLowerCase().includes(termo) || f.grupo.toLowerCase().includes(termo));
    }, [lista, busca, filtro]);

    const total = lista?.totalEmpresasAtivas ?? 0;

    /* Sem toast de sucesso: a mudança já aparece no lugar (a linha, o "N de 30",
       o histórico), e o toast caía em cima do cabeçalho do painel. Erro continua em toast. */
    const depoisDeGravar = async () => {
        await carregar();
        if (chaveAberta) await carregarHistorico(chaveAberta, historicoTodo);
    };

    const executar = async (acao: () => Promise<unknown>) => {
        setTrabalhando(true);
        try {
            await acao();
            await depoisDeGravar();
            return true;
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível salvar a alteração.'));
            return false;
        } finally {
            setTrabalhando(false);
        }
    };

    const confirmarEstado = async () => {
        if (!aberta || !confirmar) return;
        const ok = await executar(() => FuncionalidadesSistemaService.mudarEstado(aberta.chave, confirmar));
        if (ok) setConfirmar(null);
    };

    const adicionarExcecao = async (empresaId: string) => {
        if (!aberta || !empresaId) return;
        // A exceção útil é a que CONTRARIA o estado global: numa ligada, desligar; nas outras, ligar.
        const ligada = aberta.estado !== 'ligada';
        const ok = await executar(() => FuncionalidadesSistemaService.definirExcecao(aberta.chave, Number(empresaId), ligada));
        if (ok) setAdicionando(false);
    };

    const opcoesEmpresa = useMemo(() => {
        const ja = new Set(aberta?.excecoes.map(e => e.empresaId) ?? []);
        return empresas
            .filter(e => !ja.has(e.id))
            .map(e => ({ value: String(e.id), label: e.nome }));
    }, [empresas, aberta]);

    /* ── o diálogo da mudança global: sempre com os três números (A3) ── */
    const conteudoDaConfirmacao = () => {
        if (!aberta || !confirmar) return null;
        const agora = aberta.empresasComAcesso;
        const depois = acessoCom(aberta, confirmar, total);
        const delta = depois - agora;
        const n = aberta.excecoes.length;
        const perdem = aberta.excecoes.filter(e => e.ligada);
        const nomesPerdem = aberta.estado === 'so_escolhidas' && confirmar === 'desligada' && perdem.length <= 3
            ? `: ${perdem.map(e => e.empresaNome ?? `empresa ${e.empresaId}`).join(' e ')}` : '';

        return (
            <ul className="sis-fn-conf">
                <li>
                    {delta < 0 && <><b>{-delta} {-delta === 1 ? 'empresa perde' : 'empresas perdem'}</b> a tela agora{nomesPerdem}.</>}
                    {delta > 0 && <><b>{delta} {delta === 1 ? 'empresa passa' : 'empresas passam'}</b> a ver {aberta.nome}.</>}
                    {delta === 0 && <>Nenhuma empresa muda de acesso agora.</>}
                    {' '}<span className="sis-fn-mut">Ficam {depois} de {total} com acesso.</span>
                </li>
                {n > 0 && confirmar === 'desligada' && (
                    <li>
                        <b>{n === 1 ? 'A exceção fica guardada' : `As ${n} exceções ficam guardadas`} e sem efeito.</b>{' '}
                        <span className="sis-fn-mut">Voltam a valer quando o estado sair de "desligada".</span>
                    </li>
                )}
                {n > 0 && confirmar !== 'desligada' && (() => {
                    /* Só conta como exceção a que CONTRARIA o novo estado: com "ligada", as
                       desligadas; com "só escolhidas", as ligadas. A outra diz o mesmo que o
                       estado e não muda nada — dizer que "continua valendo" seria mentir. */
                    const valem = aberta.excecoes.filter(e => e.ligada !== (confirmar === 'ligada')).length;
                    const inertes = n - valem;
                    const verbo = aberta.estado === 'desligada' ? 'volta' : 'continua';
                    return (
                        <li>
                            {valem > 0 && <><b>{valem === 1 ? `1 exceção ${verbo} a valer` : `${valem} exceções ${verbo}m a valer`}.</b>{' '}</>}
                            {inertes > 0 && (
                                <span className="sis-fn-mut">
                                    {inertes === 1 ? '1 exceção fica' : `${inertes} exceções ficam`} sem efeito: já {inertes === 1 ? 'diz' : 'dizem'} o mesmo que o estado novo.
                                </span>
                            )}
                        </li>
                    );
                })()}
                <li>
                    {confirmar === 'desligada'
                        ? <><b>Quem já conectou continua como está.</b> <span className="sis-fn-mut">Desligar fecha a porta; para parar uma conta, remova-a em Conexões.</span></>
                        : <><b>Pode levar até um minuto</b> <span className="sis-fn-mut">para a mudança chegar a quem está usando o sistema.</span></>}
                </li>
            </ul>
        );
    };

    const tituloConfirmacao = !aberta || !confirmar ? '' : {
        ligada: `Ligar ${aberta.nome} para todas as empresas?`,
        so_escolhidas: `Deixar ${aberta.nome} só para as empresas escolhidas?`,
        desligada: `Desligar ${aberta.nome} para todas as empresas?`,
    }[confirmar];

    const rotuloConfirmar = !confirmar ? '' : {
        ligada: 'Ligar para todas',
        so_escolhidas: 'Só para as escolhidas',
        desligada: 'Desligar para todas',
    }[confirmar];

    /* O aviso de "nada aqui" serve à tabela (no computador) e aos cartões (no
       celular) — por isso sai do meio do JSX. Esta tela não pagina: o catálogo
       é fechado e cabe inteiro. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">{lista ? 'Nada com esses filtros' : 'Não foi possível carregar'}</div>
            <p className="lk-blank-text">{lista ? 'Ajuste a busca ou o filtro.' : 'Confira se a API no ar já tem esta tela.'}</p>
        </div>
    );

    return (
        <div className="page-shell lk-screen sis-fn">
            <PageHeader
                title="Funcionalidades"
                subtitle="O que cada empresa tem no produto. Desligar fecha a porta: some da tela, e o que já está conectado segue."
            />

            {/* Sem o lk-fill das outras listagens: a lista tem sempre as 12 do catálogo, e
                esticar o cartão até o pé abriria ~200px vazios dentro dele (fase-6-tela.html §2). */}
            <div className="page-body">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar funcionalidade…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    {FILTROS.map(({ id, rotulo }) => (
                        <button key={id} type="button"
                            className={`lk-pill ${filtro === id ? 'is-on' : ''}`}
                            onClick={() => setFiltro(id)}>
                            {rotulo} <b>{contagens[id]}</b>
                        </button>
                    ))}
                </div>

                {/* Celular: as sete colunas não cabem em 358px, então a linha vira
                    cartão — a funcionalidade, de que grupo é e em que estado está.
                    O interruptor NÃO vem para o cartão: mudar o estado é ação de
                    produção, com confirmação, e isso mora no painel. */}
                {celular ? (
                    <>
                        <ListCards>
                            {carregando
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(f => (
                                    <ListCard
                                        key={f.chave}
                                        titulo={f.nome}
                                        apoio={f.grupo}
                                        estado={
                                            <>
                                                <span className={`sis-fn-st is-${f.estado}`}><i />{ROTULO_ESTADO[f.estado]}</span>
                                                <span>· {f.empresasComAcesso} de {total}</span>
                                                {f.excecoes.length > 0 && (
                                                    <span>· {f.excecoes.length} {f.excecoes.length === 1 ? 'exceção' : 'exceções'}</span>
                                                )}
                                            </>
                                        }
                                        selected={f.chave === chaveAberta}
                                        label={`Abrir ${f.nome}`}
                                        onOpen={() => abrir(f.chave)}
                                    />
                                ))}
                        </ListCards>
                        {!carregando && visiveis.length === 0 && listaVazia}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Funcionalidade</div>
                            <div className="lk-head">Grupo</div>
                            <div className="lk-head">Estado</div>
                            <div className="lk-head sis-fn-r">Com acesso</div>
                            <div className="lk-head sis-fn-r">Exceções</div>
                            <div className="lk-head">Alterado</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {carregando
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(f => (
                                    <ListRow key={f.chave} columns={COLUNAS} selected={f.chave === chaveAberta}>
                                        <span className="lk-cell lk-strong"><span>{f.nome}</span></span>
                                        <span className="lk-cell lk-muted"><span>{f.grupo}</span></span>
                                        <span className="lk-cell"><span className={`sis-fn-st is-${f.estado}`}><i />{ROTULO_ESTADO[f.estado]}</span></span>
                                        <span className="lk-cell sis-fn-r"><span>{f.empresasComAcesso} de {total}</span></span>
                                        <span className={`lk-cell sis-fn-r ${f.excecoes.length ? '' : 'sis-fn-fnt'}`}><span>{f.excecoes.length || '—'}</span></span>
                                        <span className={`lk-cell ${f.alterado ? 'lk-muted' : 'sis-fn-fnt'}`}>
                                            <span>{f.alterado && f.alteradoEm
                                                ? `${f.alteradoPorNome ?? '—'} · ${new Date(f.alteradoEm).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`
                                                : 'de fábrica'}</span>
                                        </span>
                                        <RowActions>
                                            <RowAction icon={<LuEye size={14} />} label={`Ver ${f.nome}`} onClick={() => abrir(f.chave)} />
                                        </RowActions>
                                    </ListRow>
                                ))}
                        </div>
                    </div>

                    {!carregando && visiveis.length === 0 && listaVazia}
                </div>
                )}
            </div>

            <RecordPanel
                open={!!aberta}
                onClose={fechar}
                title={aberta?.nome ?? ''}
                subtitle={aberta ? `${aberta.grupo} · ${aberta.empresasComAcesso} de ${total} empresas com acesso` : undefined}
            >
                {aberta && (
                    <>
                        <PanelSection title="Para todas as empresas">
                            <div className="sis-fn-seg" role="radiogroup" aria-label="Estado para todas as empresas">
                                {ESTADOS.map(e => (
                                    <button key={e} type="button" role="radio" aria-checked={aberta.estado === e}
                                        className={`lk-pill ${aberta.estado === e ? 'is-on' : ''}`}
                                        disabled={trabalhando}
                                        onClick={() => { if (aberta.estado !== e) setConfirmar(e); }}>
                                        {ROTULO_ESTADO[e]}
                                    </button>
                                ))}
                            </div>
                            <p className="sis-fn-hint">{SIGNIFICADO[aberta.estado]}</p>
                            {aberta.estado !== aberta.estadoFabrica && (
                                <p className="sis-fn-hint sis-fn-fnt">De fábrica: {ROTULO_ESTADO[aberta.estadoFabrica].toLowerCase()}.</p>
                            )}
                        </PanelSection>

                        <PanelSection
                            title="Empresas com exceção"
                            extra={!adicionando && (
                                <button type="button" className="sis-fn-lnk" onClick={() => setAdicionando(true)}>
                                    <LuPlus size={13} /> Adicionar empresa
                                </button>
                            )}
                        >
                            {adicionando && (
                                <div className="sis-fn-add">
                                    <Select
                                        options={opcoesEmpresa}
                                        value=""
                                        onChange={v => void adicionarExcecao(v)}
                                        placeholder={empresas.length ? 'Escolha a empresa…' : 'Carregando empresas…'}
                                        searchFrom={0}
                                        size="sm"
                                        disabled={trabalhando || empresas.length === 0}
                                        emptyText="Todas as empresas já têm exceção."
                                        ariaLabel="Empresa"
                                    />
                                    <button type="button" className="rp-btn" onClick={() => setAdicionando(false)}>Cancelar</button>
                                </div>
                            )}
                            {adicionando && (
                                <p className="sis-fn-hint">
                                    Entra {aberta.estado === 'ligada' ? 'desligada' : 'ligada'}, o contrário do estado de cima. O interruptor troca depois.
                                </p>
                            )}

                            {aberta.excecoes.length === 0 && !adicionando && (
                                <p className="rp-none">Nenhuma. Todas as empresas seguem o estado de cima.</p>
                            )}
                            {aberta.excecoes.map(e => (
                                <div key={e.empresaId} className="sis-fn-exc">
                                    <span className="sis-fn-exc-nome">
                                        {e.empresaNome ?? `Empresa ${e.empresaId}`}
                                        {/* Sem efeito: o estado global é "desligada", ou a exceção diz o mesmo que ele. */}
                                        {(aberta.estado === 'desligada' || e.ligada === (aberta.estado === 'ligada')) && (
                                            <span className="sis-fn-exc-inerte"> · sem efeito</span>
                                        )}
                                    </span>
                                    <span className="sis-fn-exc-sw">
                                        <span className="sis-fn-exc-lbl">{e.ligada ? 'ligada' : 'desligada'}</span>
                                        <Switch checked={e.ligada} label={`${aberta.nome} para ${e.empresaNome ?? e.empresaId}`}
                                            disabled={trabalhando}
                                            onChange={v => void executar(() => FuncionalidadesSistemaService.definirExcecao(aberta.chave, e.empresaId, v))} />
                                    </span>
                                    <button type="button" className="lk-act is-danger" aria-label={`Tirar a exceção de ${e.empresaNome ?? e.empresaId}`}
                                        title="Tirar a exceção" disabled={trabalhando}
                                        onClick={() => void executar(() => FuncionalidadesSistemaService.removerExcecao(aberta.chave, e.empresaId))}>
                                        <LuTrash2 size={14} />
                                    </button>
                                </div>
                            ))}
                            {aberta.excecoes.length > 0 && (
                                <p className="sis-fn-hint">
                                    {aberta.estado === 'desligada'
                                        ? <>Com o estado em "desligada", estas exceções ficam guardadas e <b>sem efeito</b>.</>
                                        : 'Tirar a exceção devolve a empresa ao estado de cima.'}
                                </p>
                            )}
                        </PanelSection>

                        <PanelSection
                            title="Histórico"
                            extra={!historicoTodo && historico.length >= 5 && (
                                <button type="button" className="sis-fn-lnk"
                                    onClick={() => { setHistoricoTodo(true); void carregarHistorico(aberta.chave, true); }}>
                                    <LuHistory size={13} /> Ver tudo
                                </button>
                            )}
                        >
                            {historico.length === 0
                                ? <p className="rp-none">Nunca mexida: vale o estado de fábrica.</p>
                                : (
                                    <div className="sis-fn-hist">
                                        {historico.map(m => (
                                            <div key={m.id} className="sis-fn-hist-l">
                                                <span className="sis-fn-hist-q">{dataCurta(m.em)}</span>
                                                <span>{frasesDoHistorico(m)}{m.impersonatedBy !== null && <span className="sis-fn-fnt"> · em modo suporte</span>}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                        </PanelSection>
                    </>
                )}
            </RecordPanel>

            <Dialog
                open={!!confirmar && !!aberta}
                onClose={() => { if (!trabalhando) setConfirmar(null); }}
                title={tituloConfirmacao}
                confirmLabel={rotuloConfirmar}
                danger={confirmar === 'desligada'}
                busy={trabalhando}
                onConfirm={() => void confirmarEstado()}
            >
                {conteudoDaConfirmacao()}
            </Dialog>
        </div>
    );
};

export default FuncionalidadesSistemaPage;
